import { Injectable, signal, computed } from '@angular/core';
import { CurrentUser } from './auth.types';
import { clearSessionExpiredRedirect } from '../services/session-expiry.state';

const STORAGE_KEY = 'vendorAuthData';
const REMEMBER_ME_KEY = 'loginRememberedEmail';

/**
 * Stores the authenticated user, persisted to sessionStorage so a hard refresh
 * (F5) does not log the user out.
 *
 * Why sessionStorage (not localStorage): it is cleared when the tab/window
 * closes, bounding token lifetime, while surviving in-page reloads. The token
 * is short-lived (~15 min) and the app has no working refresh-on-load path
 * (the backend /auth/refresh depends on Redis, currently down), so persisting
 * here is the pragmatic fix for the "refresh → login page" bug.
 *
 * Future hardening: move to an httpOnly refresh cookie that silently
 * re-authenticates on boot, then this can go back to memory-only.
 */
@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private readonly _user = signal<CurrentUser | null>(this.readFromStorage());

  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);

  getUser(): CurrentUser | null {
    return this._user();
  }

  getToken(): string | null {
    return this._user()?.token ?? null;
  }

  setUser(user: CurrentUser): void {
    this._user.set(user);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } catch {
      /* storage unavailable (private mode / quota) — stays in memory only */
    }
    // A new session just started — re-arm the interceptor's redirect guard so
    // a later 401 (e.g. this token also expiring) redirects again instead of
    // being silently swallowed by the previous session's already-tripped flag.
    clearSessionExpiredRedirect();
  }

  clearUser(): void {
    this._user.set(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  getRememberedEmail(): string | null {
    try {
      return localStorage.getItem(REMEMBER_ME_KEY);
    } catch {
      return null;
    }
  }

  setRememberedEmail(email: string): void {
    try {
      localStorage.setItem(REMEMBER_ME_KEY, email);
    } catch {
      /* storage unavailable */
    }
  }

  clearRememberedEmail(): void {
    try {
      localStorage.removeItem(REMEMBER_ME_KEY);
    } catch {
      /* ignore */
    }
  }

  /** Restore a persisted session on app boot so a refresh keeps the user in. */
  private readFromStorage(): CurrentUser | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CurrentUser) : null;
    } catch {
      return null;
    }
  }
}
